import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ContractorReferralWorkQueueApi from "../../../../../infrastructure/api/contractorReferralWorkQueue";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import { getCurrentUserId } from "../../../../components/request/requestAssignmentDisplay";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const PAGE_TITLE = "کارتابل ارجاع و قیمت پیمانکار";
const OPERATIONS_SOURCE = "SolarentOperator";

export const QUEUE_TABS = [
  {
    key: "awaitingReferral",
    label: "در انتظار ارجاع به پیمانکار",
    countField: "awaitingReferralCount",
  },
  {
    key: "reviewPrice",
    label: "بررسی قیمت پیمانکار",
    countField: "reviewPriceCount",
  },
  {
    key: "approved",
    label: "تایید شده",
    countField: "approvedCount",
  },
  {
    key: "rejected",
    label: "رد شده",
    countField: "rejectedCount",
  },
];

const EMPTY_COUNTS = {
  awaitingReferralCount: 0,
  reviewPriceCount: 0,
  approvedCount: 0,
  rejectedCount: 0,
};

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

const formatRial = (value) =>
  Number(value) > 0 ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

const normalizeQueueTab = (raw) => {
  const tab = pick(raw, "queueTab", "QueueTab") || "awaitingReferral";
  if (tab === "pendingDecision") return "reviewPrice";
  return tab;
};

export const normalizeQueueRow = (raw) => ({
  requestInfoId: pick(raw, "requestInfoId", "RequestInfoId"),
  requestTrackingCode: pick(raw, "requestTrackingCode", "RequestTrackingCode"),
  applicantFullName: pick(raw, "applicantFullName", "ApplicantFullName") || "—",
  applicantMobile: pick(raw, "applicantMobile", "ApplicantMobile") || "",
  plantCapacityKw: pick(raw, "plantCapacityKw", "PlantCapacityKw"),
  requestStepTitle: pick(raw, "requestStepTitle", "RequestStepTitle") || "",
  distributionCompanyId: pick(raw, "distributionCompanyId", "DistributionCompanyId"),
  distributionCompanyName:
    pick(raw, "distributionCompanyName", "DistributionCompanyName") || "",
  queueTab: normalizeQueueTab(raw),
  assignedToUserId: pick(raw, "assignedToUserId", "AssignedToUserId"),
  assignedToFullName: pick(raw, "assignedToFullName", "AssignedToFullName") || "",
  assignedAt: pick(raw, "assignedAt", "AssignedAt"),
  suggestedContractorInfoId: pick(
    raw,
    "suggestedContractorInfoId",
    "SuggestedContractorInfoId"
  ),
  suggestedContractorName:
    pick(raw, "suggestedContractorName", "SuggestedContractorName") || "",
  referredContractorInfoId: pick(
    raw,
    "referredContractorInfoId",
    "ReferredContractorInfoId"
  ),
  referredContractorName:
    pick(raw, "referredContractorName", "ReferredContractorName") || "",
  assignmentId: pick(raw, "assignmentId", "AssignmentId"),
  assignmentStatusTitle:
    pick(raw, "assignmentStatusTitle", "AssignmentStatusTitle") || "",
  rejectReason: pick(raw, "rejectReason", "RejectReason") || "",
  costQuoteId: pick(raw, "costQuoteId", "CostQuoteId"),
  costQuoteStatus: pick(raw, "costQuoteStatus", "CostQuoteStatus"),
  costQuoteStatusTitle:
    pick(raw, "costQuoteStatusTitle", "CostQuoteStatusTitle") || "",
  totalContractorCost: Number(pick(raw, "totalContractorCost", "TotalContractorCost")) || 0,
  costQuoteSubmittedAt: pick(raw, "costQuoteSubmittedAt", "CostQuoteSubmittedAt"),
  canReviewCostQuote: Boolean(pick(raw, "canReviewCostQuote", "CanReviewCostQuote")),
  needsFinalApproval: Boolean(pick(raw, "needsFinalApproval", "NeedsFinalApproval")),
  customerPriceOfferId: pick(raw, "customerPriceOfferId", "CustomerPriceOfferId"),
  customerPriceOfferStatus: pick(
    raw,
    "customerPriceOfferStatus",
    "CustomerPriceOfferStatus"
  ),
  customerPriceOfferStatusTitle:
    pick(raw, "customerPriceOfferStatusTitle", "CustomerPriceOfferStatusTitle") || "",
  customerPriceFinal: Number(pick(raw, "customerPriceFinal", "CustomerPriceFinal")) || 0,
  enteredQueueAt: pick(raw, "enteredQueueAt", "EnteredQueueAt"),
  lastActionAt: pick(raw, "lastActionAt", "LastActionAt"),
  openDays: pick(raw, "openDays", "OpenDays") ?? 0,
});

const workspacePricingPath = (requestInfoId) =>
  `/SLN/SolarentOperator/ProjectWorkspace/${requestInfoId}?view=all&section=quotes-contract&recalc=1`;

const ContractorReferralWorkQueuePage = () => {
  const navigate = useNavigate();
  const currentUserId = getCurrentUserId();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [activeTab, setActiveTab] = useState("awaitingReferral");
  const [experts, setExperts] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [assignDialog, setAssignDialog] = useState({ open: false, row: null, userId: "" });
  const [referDialog, setReferDialog] = useState({
    open: false,
    row: null,
    contractorId: "",
    note: "",
  });
  const [rejectDialog, setRejectDialog] = useState({ open: false, row: null, reason: "" });
  const [busyRequestId, setBusyRequestId] = useState(null);

  const loadFilterData = useCallback(async () => {
    try {
      const res = await ContractorReferralWorkQueueApi.getFilterData(OPERATIONS_SOURCE);
      const data = res?.data ?? res;
      setExperts(Array.isArray(data?.experts ?? data?.Experts) ? data.experts ?? data.Experts : []);
      setContractors(
        Array.isArray(data?.contractors ?? data?.Contractors)
          ? data.contractors ?? data.Contractors
          : []
      );
    } catch {
      setExperts([]);
      setContractors([]);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (appliedSearch.trim()) payload.searchText = appliedSearch.trim();
      if (assigneeFilter !== "") payload.assignedToUserId = Number(assigneeFilter);

      const res = await ContractorReferralWorkQueueApi.search(OPERATIONS_SOURCE, payload);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در دریافت کارتابل ارجاع و قیمت پیمانکار");
        setRows([]);
        setCounts(EMPTY_COUNTS);
        return;
      }

      const data = res?.data ?? res;
      const list = data?.rows ?? data?.Rows ?? [];
      setRows(Array.isArray(list) ? list.map(normalizeQueueRow) : []);
      setCounts({
        awaitingReferralCount:
          data?.awaitingReferralCount ?? data?.AwaitingReferralCount ?? 0,
        reviewPriceCount:
          data?.reviewPriceCount ??
          data?.ReviewPriceCount ??
          data?.pendingDecisionCount ??
          data?.PendingDecisionCount ??
          0,
        approvedCount: data?.approvedCount ?? data?.ApprovedCount ?? 0,
        rejectedCount: data?.rejectedCount ?? data?.RejectedCount ?? 0,
      });
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت کارتابل ارجاع و قیمت پیمانکار");
      setRows([]);
      setCounts(EMPTY_COUNTS);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, assigneeFilter]);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAssign = useCallback(
    async (row, assignedToUserId) => {
      if (!row?.requestInfoId) return;
      setBusyRequestId(row.requestInfoId);
      try {
        const res = await ContractorReferralWorkQueueApi.assign(
          OPERATIONS_SOURCE,
          row.requestInfoId,
          assignedToUserId || null
        );
        if (res?.isSuccess === false) {
          toast.error(res?.message || "خطا در ثبت مسئول پیگیری");
          return;
        }
        toast.success(res?.message || "مسئول پیگیری ثبت شد");
        await loadList();
      } catch (e) {
        toast.error(e?.message || "خطا در ثبت مسئول پیگیری");
      } finally {
        setBusyRequestId(null);
      }
    },
    [loadList]
  );

  const openReferDialog = useCallback((row) => {
    const suggested = row?.suggestedContractorInfoId || "";
    setReferDialog({
      open: true,
      row,
      contractorId: suggested || row?.referredContractorInfoId || "",
      note: "",
    });
  }, []);

  const handleRefer = async () => {
    const { row, contractorId, note } = referDialog;
    if (!row?.requestInfoId || !contractorId) {
      toast.error("پیمانکار را انتخاب کنید");
      return;
    }
    setBusyRequestId(row.requestInfoId);
    try {
      const res = await ContractorReferralWorkQueueApi.refer(OPERATIONS_SOURCE, {
        requestId: row.requestInfoId,
        contractorInfoId: Number(contractorId),
        note,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "ارجاع ناموفق بود");
        return;
      }
      toast.success(res?.message || "ارجاع ثبت شد");
      setReferDialog({ open: false, row: null, contractorId: "", note: "" });
      setActiveTab("reviewPrice");
      await loadList();
    } catch (e) {
      toast.error(e?.message || "ارجاع ناموفق بود");
    } finally {
      setBusyRequestId(null);
    }
  };

  const handleApprovePrice = useCallback(
    async (row) => {
      if (!row?.requestInfoId) return;
      if (!row.canReviewCostQuote || !row.costQuoteId) {
        toast.info("ابتدا پیمانکار باید پیشنهاد قیمت را ارسال کند.");
        return;
      }
      setBusyRequestId(row.requestInfoId);
      try {
        const res = await ContractorReferralWorkQueueApi.approve(OPERATIONS_SOURCE, {
          requestId: row.requestInfoId,
          costQuoteId: row.costQuoteId,
          reason: "تأیید قیمت پیمانکار از کارتابل",
        });
        if (res?.isSuccess === false) {
          toast.error(res?.message || "تأیید قیمت ناموفق بود");
          return;
        }
        toast.success(res?.message || "قیمت پیمانکار تأیید شد");
        setActiveTab("approved");
        await loadList();
      } catch (e) {
        toast.error(e?.message || "تأیید قیمت ناموفق بود");
      } finally {
        setBusyRequestId(null);
      }
    },
    [loadList]
  );

  const handleRejectConfirm = async () => {
    const { row, reason } = rejectDialog;
    if (!row?.requestInfoId) return;
    if (!String(reason || "").trim()) {
      toast.error("دلیل رد الزامی است");
      return;
    }
    setBusyRequestId(row.requestInfoId);
    try {
      const res = await ContractorReferralWorkQueueApi.reject(OPERATIONS_SOURCE, {
        requestId: row.requestInfoId,
        costQuoteId: row.costQuoteId || null,
        reason: String(reason).trim(),
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "رد ناموفق بود");
        return;
      }
      toast.success(res?.message || "رد شد");
      setRejectDialog({ open: false, row: null, reason: "" });
      setActiveTab("rejected");
      await loadList();
    } catch (e) {
      toast.error(e?.message || "رد ناموفق بود");
    } finally {
      setBusyRequestId(null);
    }
  };

  const tabRows = useMemo(
    () => rows.filter((r) => r.queueTab === activeTab),
    [rows, activeTab]
  );

  const tableRows = useMemo(
    () =>
      tabRows.map((row, index) => ({
        ...row,
        row_index: index + 1,
        capacityDisplay: row.plantCapacityKw ? `${row.plantCapacityKw} kW` : "—",
        distributionCompanyDisplay: row.distributionCompanyName || "—",
        suggestedDisplay: row.suggestedContractorName || "—",
        referredDisplay: row.referredContractorName || "—",
        costStatusDisplay: row.costQuoteStatusTitle || "بدون پیشنهاد",
        costAmountDisplay: formatRial(row.totalContractorCost),
        customerPriceDisplay: formatRial(row.customerPriceFinal),
        assigneeDisplay: row.assignedToFullName || "بدون مسئول",
        assigneeColor: row.assignedToFullName
          ? Number(row.assignedToUserId) === Number(currentUserId)
            ? "success"
            : "info"
          : "default",
        lastActionDisplay: row.lastActionAt ? toJalaliFullDate(row.lastActionAt) : "—",
        openDaysDisplay: `${row.openDays} روز`,
        rejectReasonDisplay: row.rejectReason || "—",
        finalStatusDisplay: row.needsFinalApproval
          ? "نیاز به محاسبه و تایید نهایی"
          : row.customerPriceOfferStatusTitle || "—",
      })),
    [tabRows, currentUserId]
  );

  const headCells = useMemo(() => {
    const base = [
      { id: "row_index", label: "ردیف" },
      { id: "requestTrackingCode", label: "کد پیگیری" },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "applicantMobile", label: "موبایل" },
      { id: "capacityDisplay", label: "ظرفیت" },
      { id: "distributionCompanyDisplay", label: "شرکت توزیع" },
      { id: "suggestedDisplay", label: "پیمانکار معرف" },
      { id: "referredDisplay", label: "پیمانکار ارجاع‌شده" },
      {
        id: "assigneeDisplay",
        label: "مسئول پیگیری",
        type: "chip",
        model: { colorField: "assigneeColor" },
      },
      { id: "lastActionDisplay", label: "آخرین اقدام" },
      { id: "openDaysDisplay", label: "روزهای باز" },
    ];

    if (activeTab === "reviewPrice" || activeTab === "approved" || activeTab === "rejected") {
      base.splice(
        8,
        0,
        { id: "costStatusDisplay", label: "وضعیت قیمت" },
        { id: "costAmountDisplay", label: "مبلغ پیمانکار" }
      );
    }

    if (activeTab === "approved") {
      base.splice(10, 0, { id: "customerPriceDisplay", label: "قیمت مشتری" });
      base.splice(11, 0, {
        id: "finalStatusDisplay",
        label: "وضعیت تایید نهایی",
        render: (value, row) =>
          row.needsFinalApproval ? (
            <Chip size="small" color="warning" label={value} />
          ) : (
            value
          ),
      });
    }

    if (activeTab === "rejected") {
      base.splice(8, 0, { id: "rejectReasonDisplay", label: "دلیل رد" });
    }

    return base;
  }, [activeTab]);

  const tableActions = useMemo(
    () => [
      {
        icon: <OpenInNewIcon fontSize="small" />,
        onClick: (row) =>
          navigate(`/SLN/SolarentOperator/ProjectWorkspace/${row.requestInfoId}?view=all`),
        color: "primary",
        tooltip: "باز کردن پرونده",
      },
      {
        icon: <HandshakeIcon fontSize="small" />,
        onClick: (row) => openReferDialog(row),
        color: "secondary",
        tooltip: "ارجاع به پیمانکار",
        hidden: (row) =>
          busyRequestId === row.requestInfoId ||
          (row.queueTab !== "awaitingReferral" && row.queueTab !== "rejected"),
      },
      {
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        onClick: (row) => handleApprovePrice(row),
        color: "success",
        tooltip: "تأیید قیمت پیمانکار",
        hidden: (row) =>
          busyRequestId === row.requestInfoId ||
          row.queueTab !== "reviewPrice" ||
          !row.canReviewCostQuote,
      },
      {
        icon: <HighlightOffIcon fontSize="small" />,
        onClick: (row) => setRejectDialog({ open: true, row, reason: "" }),
        color: "error",
        tooltip: "رد قیمت / ارجاع",
        hidden: (row) =>
          busyRequestId === row.requestInfoId || row.queueTab !== "reviewPrice",
      },
      {
        icon: <CalculateOutlinedIcon fontSize="small" />,
        onClick: (row) => navigate(workspacePricingPath(row.requestInfoId)),
        color: "warning",
        tooltip: "محاسبه و تایید نهایی قیمت مشتری",
        hidden: (row) =>
          busyRequestId === row.requestInfoId ||
          row.queueTab !== "approved" ||
          !row.needsFinalApproval,
      },
      {
        icon: <AssignmentIndIcon fontSize="small" />,
        onClick: (row) => handleAssign(row, null),
        color: "success",
        tooltip: "پیگیری با من",
        hidden: (row) =>
          busyRequestId === row.requestInfoId ||
          Number(row.assignedToUserId) === Number(currentUserId),
      },
      {
        icon: <PersonAddAlt1Icon fontSize="small" />,
        onClick: (row) =>
          setAssignDialog({ open: true, row, userId: row.assignedToUserId || "" }),
        color: "info",
        tooltip: "ارجاع به کارشناس",
        hidden: (row) => busyRequestId === row.requestInfoId,
      },
    ],
    [
      busyRequestId,
      currentUserId,
      handleApprovePrice,
      handleAssign,
      navigate,
      openReferDialog,
    ]
  );

  const contractorOptions = useMemo(
    () =>
      (contractors || []).map((c) => ({
        id: c.id ?? c.Id,
        title:
          c.title ??
          c.Title ??
          c.companyName ??
          c.CompanyName ??
          `#${c.id ?? c.Id}`,
      })),
    [contractors]
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="ارجاع به پیمانکار، بررسی قیمت پیشنهادی، محاسبه و تایید نهایی قیمت مشتری"
            action={
              <Button
                startIcon={<SyncIcon />}
                onClick={loadList}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                بروزرسانی
              </Button>
            }
          />
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <TextField
                size="small"
                placeholder="جستجو نام / موبایل / کد / پیمانکار / شرکت توزیع"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setAppliedSearch(searchText)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280 }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>مسئول پیگیری</InputLabel>
                <Select
                  label="مسئول پیگیری"
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  {experts.map((ex) => (
                    <MenuItem key={ex.id ?? ex.Id} value={ex.id ?? ex.Id}>
                      {ex.fullName ?? ex.FullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={() => setAppliedSearch(searchText)}>
                اعمال فیلتر
              </Button>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {QUEUE_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={
                    <Badge
                      badgeContent={counts[tab.countField] || 0}
                      color={
                        tab.key === "rejected"
                          ? "error"
                          : tab.key === "approved"
                            ? "success"
                            : "primary"
                      }
                      sx={{ "& .MuiBadge-badge": { right: -12 } }}
                    >
                      <Typography component="span" sx={{ pr: 1.5 }}>
                        {tab.label}
                      </Typography>
                    </Badge>
                  }
                />
              ))}
            </Tabs>

            {activeTab === "reviewPrice" ? (
              <Stack sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  پس از ارجاع، پیمانکار پیشنهاد هزینه را ثبت می‌کند. وقتی ارسال شد، از همین تب
                  تأیید یا رد کنید. برای ردیف‌های بدون پیشنهاد، پرونده را باز کنید یا منتظر ارسال
                  پیمانکار بمانید.
                </Typography>
              </Stack>
            ) : null}

            {activeTab === "approved" ? (
              <Stack sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  هزینه پیمانکار تأیید شده است. برای ردیف‌هایی که «نیاز به محاسبه و تایید نهایی»
                  دارند، دکمه محاسبه را بزنید تا قیمت مشتری در پرونده محاسبه و تأیید شود.
                </Typography>
              </Stack>
            ) : null}

            <EnhancedTable
              rows={tableRows}
              headCells={headCells}
              actions={tableActions}
              actionsMode="menu"
              loading={loading}
              showCheckbox={false}
              dense
            />
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false, row: null, userId: "" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>ارجاع به کارشناس</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>کارشناس</InputLabel>
            <Select
              label="کارشناس"
              value={assignDialog.userId}
              onChange={(e) => setAssignDialog((s) => ({ ...s, userId: e.target.value }))}
            >
              {experts.map((ex) => (
                <MenuItem key={ex.id ?? ex.Id} value={ex.id ?? ex.Id}>
                  {ex.fullName ?? ex.FullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, row: null, userId: "" })}>
            انصراف
          </Button>
          <Button
            variant="contained"
            disabled={!assignDialog.userId}
            onClick={async () => {
              const { row, userId } = assignDialog;
              setAssignDialog({ open: false, row: null, userId: "" });
              if (row && userId) await handleAssign(row, Number(userId));
            }}
          >
            ثبت
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={referDialog.open}
        onClose={() => setReferDialog({ open: false, row: null, contractorId: "", note: "" })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>ارجاع به پیمانکار</DialogTitle>
        <DialogContent>
          {referDialog.row?.suggestedContractorName ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 1 }}>
              پیمانکار معرف: <strong>{referDialog.row.suggestedContractorName}</strong>
            </Typography>
          ) : null}
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel>پیمانکار</InputLabel>
            <Select
              label="پیمانکار"
              value={referDialog.contractorId}
              onChange={(e) => setReferDialog((s) => ({ ...s, contractorId: e.target.value }))}
            >
              {contractorOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="یادداشت (اختیاری)"
            value={referDialog.note}
            onChange={(e) => setReferDialog((s) => ({ ...s, note: e.target.value }))}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReferDialog({ open: false, row: null, contractorId: "", note: "" })}
          >
            انصراف
          </Button>
          <Button variant="contained" onClick={handleRefer} disabled={!referDialog.contractorId}>
            ثبت ارجاع
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, row: null, reason: "" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {rejectDialog.row?.canReviewCostQuote ? "رد قیمت پیمانکار" : "رد ارجاع"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="دلیل رد"
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog((s) => ({ ...s, reason: e.target.value }))}
            multiline
            minRows={3}
            sx={{ mt: 1 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, row: null, reason: "" })}>
            انصراف
          </Button>
          <Button color="error" variant="contained" onClick={handleRejectConfirm}>
            ثبت رد
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ContractorReferralWorkQueuePage;
